import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { nanoid } from 'nanoid';

const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY || '';

interface PaymentPageProps {}

const PaperPaymentPage: React.FC<PaymentPageProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentWidgetRef = useRef<any>(null);
  const paymentMethodsWidgetRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // URL 파라미터에서 시험지 정보 추출
  const paperId = searchParams.get('paperId');
  const paperTitle = searchParams.get('paperTitle');
  const price = Number(searchParams.get('price')) || 0;
  const quantity = Number(searchParams.get('quantity')) || 1;
  const academyId = searchParams.get('academyId');

  const totalAmount = price * quantity;
  const orderId = nanoid(); // 주문 고유 ID 생성

  useEffect(() => {
    if (!paperId || !paperTitle || !academyId || totalAmount <= 0) {
      message.error('결제 정보가 올바르지 않습니다.');
      navigate('/academy/paper/list');
      return;
    }

    const customerKey = `academy_${academyId}_${Date.now()}`; // 고객 고유 ID

    // SDK 로드 확인 및 초기화
    const initPaymentWidget = () => {
      if (typeof (window as any).loadPaymentWidget === 'undefined') {
        console.error('토스페이먼츠 SDK가 로드되지 않았습니다.');
        message.error('결제 시스템을 불러올 수 없습니다. 페이지를 새로고침해주세요.');
        setIsLoading(false);
        return;
      }

      (window as any).loadPaymentWidget(clientKey, customerKey)
        .then((paymentWidget: any) => {
          paymentWidgetRef.current = paymentWidget;

          // 결제 UI 렌더링
          const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
            '#payment-widget',
            { value: totalAmount },
            { variantKey: 'DEFAULT' }
          );
          paymentMethodsWidgetRef.current = paymentMethodsWidget;

          // 이용약관 UI 렌더링
          paymentWidget.renderAgreement('#agreement');

          setIsLoading(false);
        })
        .catch((error: any) => {
          console.error('결제 위젯 로드 실패:', error);
          message.error('결제 위젯을 불러오는데 실패했습니다.');
          setIsLoading(false);
        });
    };

    // SDK 스크립트 로드 대기
    if (typeof (window as any).loadPaymentWidget !== 'undefined') {
      initPaymentWidget();
    } else {
      // SDK 스크립트가 로드될 때까지 대기
      const checkSDK = setInterval(() => {
        if (typeof (window as any).loadPaymentWidget !== 'undefined') {
          clearInterval(checkSDK);
          initPaymentWidget();
        }
      }, 100);

      // 5초 후 타임아웃
      setTimeout(() => {
        clearInterval(checkSDK);
        if (typeof (window as any).loadPaymentWidget === 'undefined') {
          message.error('결제 시스템을 불러올 수 없습니다. 페이지를 새로고침해주세요.');
          setIsLoading(false);
        }
      }, 5000);
    }
  }, [paperId, paperTitle, academyId, totalAmount, navigate, clientKey]);

  const handlePayment = async () => {
    const paymentWidget = paymentWidgetRef.current;

    if (!paymentWidget) {
      message.error('결제 위젯이 준비되지 않았습니다.');
      return;
    }

    try {
      // 결제 요청
      await paymentWidget.requestPayment({
        orderId: orderId,
        orderName: `${paperTitle} (x${quantity})`,
        customerName: '학원',
        successUrl: `${window.location.origin}/academy/paper/payment/success?paperId=${paperId}&quantity=${quantity}&academyId=${academyId}`,
        failUrl: `${window.location.origin}/academy/paper/payment/fail`,
      });
    } catch (error) {
      console.error('결제 요청 실패:', error);
      message.error('결제 요청에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>결제 준비 중...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '20px' }}>
      <h2 style={{ marginBottom: '24px' }}>시험지 구매</h2>

      <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '24px' }}>
        <div style={{ marginBottom: '8px' }}>
          <strong>상품명:</strong> {paperTitle}
        </div>
        <div style={{ marginBottom: '8px' }}>
          <strong>수량:</strong> {quantity}개
        </div>
        <div style={{ marginBottom: '8px' }}>
          <strong>단가:</strong> {price.toLocaleString()}원
        </div>
        <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
          <strong>총 결제금액:</strong> {totalAmount.toLocaleString()}원
        </div>
      </div>

      {/* 결제 위젯 UI */}
      <div id="payment-widget" style={{ marginBottom: '24px' }} />

      {/* 이용약관 UI */}
      <div id="agreement" style={{ marginBottom: '24px' }} />

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => navigate('/academy/paper/list')}
          style={{
            flex: 1,
            padding: '12px',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          취소
        </button>
        <button
          onClick={handlePayment}
          style={{
            flex: 2,
            padding: '12px',
            background: '#1890ff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          결제하기
        </button>
      </div>
    </div>
  );
};

export default PaperPaymentPage;

